import * as React from 'react';
import {
  Paper, Box, Typography, List, ListItemButton, ListItemText, Divider,
  IconButton, CircularProgress, TextField, InputAdornment, Chip, Stack, Button,
  Tabs, Tab, useMediaQuery,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTheme } from '@mui/material/styles';

import { useI18n } from '../../i18n/I18nProvider';
import pricelistI18n from '../../i18n/components/tarifs/pricelist.i18n';
import { usePricingData } from '../../context/PricingDataContext';

const COMPACT_ITEM_HEIGHT = 38;
const DEBUG_PRICELIST = true;

const PART_TABS = ['screen', 'motherboard', 'charging', 'camera'];

const partLabel = (t, value) => {
  if (value === 'screen') return t('tarifs.pricelist.parts.tabs.screen', 'Écrans');
  if (value === 'motherboard') return t('tarifs.pricelist.parts.tabs.motherboard', 'Carte mère');
  if (value === 'charging') return t('tarifs.pricelist.parts.tabs.charging', 'Connecteur charge');
  if (value === 'camera') return t('tarifs.pricelist.parts.tabs.camera', 'Caméra');
  return value;
};

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove accents
    .replace(/['’]/g, ' ')            // unify apostrophes
    .replace(/[^a-z0-9]+/g, ' ')      // keep alnum, replace others with spaces
    .replace(/\s+/g, ' ')
    .trim();

// Component names are entered in admin per-model (ex: "Carte mère", "Connecteur de charge", "Caméra").
// For the public PriceList tabs, we classify components by (mostly) exact normalized matches.
const COMPONENT_KIND_ALIASES = {
  motherboard: [
    'carte mere',
    'motherboard',
    'mainboard',
    'logic board',
  ],
  charging: [
    'connecteur de charge',
    'connecteur charge',
    'port de charge',
    'port charge',
    'charging port',
    'charging connector',
    'dock connector',
  ],
  camera: [
    'camera',
    'caméra',
    'camera module',
    'module camera',
  ],
};

function componentKind(componentName) {
  const s = normalize(componentName);
  if (!s) return 'other';

  // Exact (normalized) matches first
  if (COMPONENT_KIND_ALIASES.motherboard.some((x) => normalize(x) === s)) return 'motherboard';
  if (COMPONENT_KIND_ALIASES.charging.some((x) => normalize(x) === s)) return 'charging';
  if (COMPONENT_KIND_ALIASES.camera.some((x) => normalize(x) === s)) return 'camera';

  // Small safety net for slightly different inputs while staying strict
  if (s === 'carte mere' || s.includes('carte mere')) return 'motherboard';
  if (s.includes('connecteur') && s.includes('charge')) return 'charging';
  if (s === 'camera' || s === 'camera arriere' || s === 'camera avant' || s.includes('camera')) return 'camera';

  // Extra tolerance for real-world admin inputs
  if (s.replace(/\s+/g, '') === 'cartemere') return 'motherboard';
  if (s.includes('mother') && s.includes('board')) return 'motherboard';
  if ((s.includes('charge') && s.includes('port')) || (s.includes('charge') && s.includes('connect'))) return 'charging';
  if (s.includes('cam') && s.includes('era')) return 'camera';

  return 'other';
}

const itemSx = {
  borderRadius: 1,
  px: 0.75,
  py: 0.25,
  minHeight: COMPACT_ITEM_HEIGHT,
  '& .MuiIconButton-root': { opacity: 0.7 },
  '&:hover .MuiIconButton-root': { opacity: 1 },
  '& .MuiListItemText-primary': { fontSize: 14, fontWeight: 600, lineHeight: 1.2 },
};

const highlightSx = {
  bgcolor: 'rgba(4,102,200,0.08)',
  boxShadow: '0 0 0 1px rgba(4,102,200,0.35)',
};

const subtleCell = {
  bgcolor: '#f8f9f8',
  border: '1px solid #edf0ed',
  borderRadius: 8,
};

const priceFmt = (n, t) =>
  Number.isFinite(Number(n)) && Number(n) > 0
    ? `${Number(n).toFixed(2)} €`
    : t('tarifs.pricelist.price.onExpertise');

const stripKnownPrefix = (name) =>
  String(name || '').replace(/^\s*(ekran|écran|ecran|screen)\s+/i, '');

const variantRank = (name) => {
  const n = String(name || '').toUpperCase();
  if (/\b(ULTRA|PRO\s*MAX)\b/.test(n)) return 6;
  if (/\bMAX\b/.test(n)) return 5;
  if (/\bPRO\b/.test(n)) return 4;
  if (/\bPLUS\b/.test(n)) return 3;
  if (/\bMINI\b/.test(n)) return 2;
  return 1;
};

const extractSeries = (brand, model) => {
  const name = `${brand} ${stripKnownPrefix(model)}`.toUpperCase();
  let m = name.match(/\bIPHONE\s*(\d{1,2})\b/);
  if (m) return { family: 'IPHONE', series: parseInt(m[1], 10) };
  m = name.match(/\bGALAXY\s*([ABSZFE])\s*(\d{1,3})\b/);
  if (m) return { family: m[1], series: parseInt(m[2], 10) };
  m = name.match(/(\d{1,3})/);
  if (m) return { family: 'GEN', series: parseInt(m[1], 10) };
  return { family: 'GEN', series: -1 };
};

const sortModelsSmart = (brandName, list) => {
  return list.slice().sort((a, b) => {
    const an = stripKnownPrefix(String(a.model));
    const bn = stripKnownPrefix(String(b.model));
    const as = extractSeries(brandName, an);
    const bs = extractSeries(brandName, bn);
    if (as.series !== bs.series) return bs.series - as.series;
    const av = variantRank(an);
    const bv = variantRank(bn);
    if (av !== bv) return bv - av;
    return an.localeCompare(bn, undefined, { numeric: true, sensitivity: 'base' });
  });
};

  export default function PriceList({
    cart = {},
    onAdd,
    onRemove,
    onSetQty,
    highlightedId,
    partFilter: partFilterProp,           // ✅ contrôlé par le parent (optionnel)
    onPartFilterChange,                   // ✅ callback parent
    onRequestExpertise,                   // ✅ ouvre la popup expertise dès la sélection (+1) du lot "Envoie, on trie pour toi"
  }) {
  const { t, register } = useI18n();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));

  // "Tight" layout: from <= 600px (mobile / small tablets) we wrap controls to avoid horizontal overflow.
  const isTight = useMediaQuery('(max-width:600px)');

  // Extra-tight: very small phones
  const isXsTight = useMediaQuery('(max-width:420px)');
  React.useEffect(() => { register('tarifs', pricelistI18n); }, [register]);

  const { brands, models, components, loading, refreshing, error } = usePricingData();
  const [query, setQuery] = React.useState('');
  const [brandFilter, setBrandFilter] = React.useState('all');
  const [partFilterLocal, setPartFilterLocal] = React.useState('screen');

  // ✅ si le parent fournit partFilterProp, on l'utilise (mode contrôlé)
  const partFilter = partFilterProp ?? partFilterLocal;

  const setPartFilter = (next) => {
    if (onPartFilterChange) onPartFilterChange(next);
    else setPartFilterLocal(next);
  };

  React.useEffect(() => {
    if (!DEBUG_PRICELIST) return;
    if (partFilter === 'screen') return;
    const list = Array.isArray(components) ? components : [];
    // show first 10 component names as we interpret them
    const sample = list.slice(0, 10).map((c) => ({
      id: c?.id,
      modelId: c?.modelId ?? c?.ModelId,
      name: c?.component ?? c?.name ?? c?.Component,
      model: c?.model || c?.Model,
    }));
  }, [components, partFilter]);

  const modelsByBrand = React.useMemo(() => {
    const map = {};
    if (!models) return map;

    // on ne garde que les modèles dont la marque est active
    const activeBrandIds = new Set(
      (brands || [])
        .filter((b) => b.isActive !== false)
        .map((b) => b.id)
    );

    models.forEach((m) => {
      // ✅ on masque les modèles désactivés
      if (m.isActive === false) return;
      // ✅ on masque aussi les modèles dont la marque est inactive
      if (!activeBrandIds.has(m.brandId)) return;
      if (!map[m.brandId]) map[m.brandId] = [];
      map[m.brandId].push(m);
    });
    return map;
  }, [models, brands]);

  const buildModelLabel = (m) => {
    const rawName = stripKnownPrefix(m.model);
    const cap = m.capacityGb ? ` (${m.capacityGb} Go)` : '';

    // Écrans: on garde la logique actuelle
    if (partFilter === 'screen') {
      const prefix = t('tarifs.pricelist.prefix.screen');
      return `${prefix} ${rawName}${cap}`.trim();
    }

    // Pièces (carte mère / connecteur charge / caméra):
    // on affiche uniquement "<Pièce> <Modèle>" (sans répéter le nom du composant)
    const piece = partLabel(t, partFilter);
    return `${piece} ${rawName}${cap}`.trim();
  };

  const stop = (e) => e.stopPropagation();

  const q = query.trim().toLowerCase();
  const modelById = React.useMemo(() => {
    const map = new Map();
    (models || []).forEach((m) => map.set(String(m.id), m));
    return map;
  }, [models]);

  const componentsByBrand = React.useMemo(() => {
    const map = {};
    if (!components || !Array.isArray(components)) return map;

    // active brands only
    const activeBrandIds = new Set(
      (brands || [])
        .filter((b) => b.isActive !== false)
        .map((b) => b.id)
    );

    components.forEach((c) => {
      const cid = c?.modelId ?? c?.ModelId;
      const model = c?.model || c?.Model || modelById.get(String(cid));
      if (!model) return;
      if (model.isActive === false) return;
      if (!activeBrandIds.has(model.brandId)) return;

      const componentName = c?.component ?? c?.name ?? c?.Component ?? '';
      const kind = componentKind(componentName);
      // keep only selected kinds (and ignore "other")
      if (partFilter === 'motherboard' && kind !== 'motherboard') return;
      if (partFilter === 'charging' && kind !== 'charging') return;
      if (partFilter === 'camera' && kind !== 'camera') return;

      if (!map[model.brandId]) map[model.brandId] = [];
      map[model.brandId].push({ ...c, _model: model });
    });

    // sort per brand: by model name then component name
    Object.keys(map).forEach((bid) => {
      map[bid] = map[bid]
        .slice()
        .sort((a, b) => {
          const am = stripKnownPrefix(String(a?._model?.model || '')).toLowerCase();
          const bm = stripKnownPrefix(String(b?._model?.model || '')).toLowerCase();
          if (am !== bm) return am.localeCompare(bm, undefined, { numeric: true, sensitivity: 'base' });
          const ac = String(a?.component ?? a?.name ?? a?.Component ?? '').toLowerCase();
          const bc = String(b?.component ?? b?.name ?? b?.Component ?? '').toLowerCase();
          return ac.localeCompare(bc, undefined, { numeric: true, sensitivity: 'base' });
        });
    });

    if (DEBUG_PRICELIST) {
      const summary = Object.fromEntries(
        Object.entries(map).map(([bid, list]) => [bid, Array.isArray(list) ? list.length : 0])
      );
    }

    return map;
  }, [components, brands, modelById, partFilter]);

  const brandIdsMatching = React.useMemo(() => {
    if (!q) return new Set();
    const set = new Set();

    if (partFilter === 'screen') {
      (models || []).forEach((m) => {
        if (m.isActive === false) return;
        const hay = `${m?.brand?.brand || ''} ${m.model}`.toLowerCase();
        if (hay.includes(q)) set.add(m.brandId);
      });
      return set;
    }

    // components mode
    (components || []).forEach((c) => {
      const cid = c?.modelId ?? c?.ModelId;
      const model = c?.model || c?.Model || modelById.get(String(cid));
      if (!model) return;
      if (model.isActive === false) return;

      const componentName = c?.component ?? c?.name ?? c?.Component ?? '';
      const kind = componentKind(componentName);
      if (partFilter === 'motherboard' && kind !== 'motherboard') return;
      if (partFilter === 'charging' && kind !== 'charging') return;
      if (partFilter === 'camera' && kind !== 'camera') return;

      const brandName = String((model?.brand?.brand) || '').toLowerCase();
      const modelName = String(model?.model || '').toLowerCase();
      const compName = String(componentName).toLowerCase();
      const hay = `${brandName} ${modelName} ${compName}`;
      if (hay.includes(q)) set.add(model.brandId);
    });

    return set;
  }, [q, models, components, partFilter, modelById]);

  // Separate brands into regular and featured, and filter regular brands by search/brand filter.
  const regularBrands = React.useMemo(
    () => (brands || []).filter((b) => !b.hasPricingInfo && b.isActive !== false),
    [brands]
  );

  const featuredBrands = React.useMemo(
    () => (brands || []).filter((b) => b.hasPricingInfo && b.isActive !== false),
    [brands]
  );

  const filteredRegularBrands = React.useMemo(() => {
    return regularBrands.filter((b) => {
      // apply brand filter (Apple / Samsung / etc.)
      if (brandFilter !== 'all' && b.id !== brandFilter) return false;

      // apply text search
      if (!q) return true;
      const brandName = (b.brand || '').toLowerCase();
      return brandName.includes(q) || brandIdsMatching.has(b.id);
    });
  }, [regularBrands, brandFilter, q, brandIdsMatching]);

  // ✅ Brands that actually have at least one visible item for the current mode/search
  const visibleRegularBrands = React.useMemo(() => {
    return filteredRegularBrands.filter((b) => {
      const brandName = String(b?.brand || '');

      // Screen mode: require at least one model after search filter
      if (partFilter === 'screen') {
        let mList = (modelsByBrand[b.id] || []).filter((m) => {
          if (!q) return true;
          const hay = `${brandName} ${m.model}`.toLowerCase();
          return hay.includes(q);
        });
        return mList.length > 0;
      }

      // Components mode: require at least one component after search filter
      const cListAll = (componentsByBrand[b.id] || []);
      const cList = q
        ? cListAll.filter((c) => {
            const modelName = String(c?._model?.model || '').toLowerCase();
            const compName = String(c?.component ?? c?.name ?? c?.Component ?? '').toLowerCase();
            const hay = `${brandName} ${modelName} ${compName}`.toLowerCase();
            return hay.includes(q);
          })
        : cListAll;

      return cList.length > 0;
    });
  }, [filteredRegularBrands, partFilter, modelsByBrand, componentsByBrand, q]);

  // ✅ Ne montrer un spinner que si on N'A PAS de données (chargement initial)
  if (loading && (!brands?.length || !models?.length)) {
    return (
      <Box sx={{ p: 2, display: 'flex', gap: 1, alignItems: 'center', color: 'text.secondary' }}>
        <CircularProgress size={18} /> {t('tarifs.pricelist.loading')}
      </Box>
    );
  }
  if (error && (!brands?.length || !models?.length)) {
    return <Box sx={{ p: 2, color: 'crimson' }}>{error}</Box>;
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden', // prevent any horizontal page scroll on mobile
      }}
    >
      {/* Navbar pièces (au-dessus de l'encadré) */}
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          mb: 1.25,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderColor: 'divider',
          boxShadow: '0 10px 26px rgba(0,0,0,0.06)',
        }}
      >
        <Tabs
          value={partFilter}
          onChange={(_, v) => setPartFilter(v)}
          variant={isSm ? 'scrollable' : 'fullWidth'}
          // On very small screens, scroll buttons can create layout overflow → disable them.
          scrollButtons={isSm && !isTight ? 'auto' : false}
          allowScrollButtonsMobile={isSm && !isTight}
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            // tighter navbar on small screens to prevent horizontal page overflow
            minHeight: isXsTight ? 44 : (isTight ? 46 : 56),
            color: 'black',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
            px: isSm ? (isXsTight ? 0 : 0.25) : 0,
            '& .MuiTabs-scroller': {
              overflowX: isSm ? 'auto' : 'hidden',
              overflowY: 'hidden',
            },
            '& .MuiTabs-flexContainer': {
              alignItems: 'stretch',
              // reduce spacing between tabs on tight screens
              gap: isTight ? 0 : 0,
            },
            '& .MuiTabs-scrollButtons': { display: isSm && !isTight ? 'flex' : 'none' },
            '& .MuiTab-root': {
              minHeight: isXsTight ? 44 : (isTight ? 46 : 56),
              textTransform: 'none',
              // slightly less aggressive weight so labels fit better
              fontWeight: isTight ? 850 : 950,
              // smaller font on mobile
              fontSize: isXsTight ? 11 : (isTight ? 12 : 14),
              lineHeight: 1.1,
              color: 'black',
              // Reduce tab width on <= 600px to avoid horizontal overflow
              minWidth: isSm ? (isXsTight ? 76 : (isTight ? 88 : 120)) : 'auto',
              // tighter horizontal padding on mobile
              px: isXsTight ? 0.75 : (isTight ? 1 : 1.5),
              whiteSpace: 'nowrap',
            },
            '& .MuiTab-root.Mui-selected': { color: 'black' },
          }}
        >
          <Tab value="screen" label={partLabel(t, 'screen')} />
          <Tab value="motherboard" label={partLabel(t, 'motherboard')} />
          <Tab value="charging" label={partLabel(t, 'charging')} />
          <Tab value="camera" label={partLabel(t, 'camera')} />
        </Tabs>
      </Paper>

      {/* Encadré principal */}
      <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.25 }, borderRadius: 2 }}>
        <Box sx={{ mb: 1, px: 0.5 }}>
          {/* <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 800, fontSize: 16 }}>{t('tarifs.pricelist.title')}</Typography>
            {refreshing && <Chip size="small" label={t('tarifs.pricelist.refreshing', 'MAJ…')} />}
          </Stack> */}
        </Box>

        {/* Filtres pièces (Écran / Carte mère / Connecteur charge / Caméra) + marques + recherche */}
        <Box sx={{ px: isTight ? 0.5 : 1, pb: 1 }}>
          {/* Boutons de filtre de marque (Apple / Samsung / etc.) */}
          {regularBrands.length > 0 && (
            <Box sx={{ mb: 1, width: '100%' }}>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  // From <= 600px we wrap into 2 columns/rows to avoid page horizontal scroll
                  flexWrap: isTight ? 'wrap' : 'nowrap',
                  gap: isTight ? 0.5 : 0.75,
                  overflowX: isTight ? 'hidden' : (isSm ? 'auto' : 'visible'),
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  pr: isSm ? 0.5 : 0,
                  // hide scrollbar (best effort)
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                }}
              >
                <Button
                  size="small"
                  variant={brandFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setBrandFilter('all')}
                  sx={{
                    flex: isTight ? '1 1 calc(50% - 6px)' : (isSm ? '0 0 auto' : '1 1 0'),
                    minWidth: isXsTight ? 0 : (isSm ? 92 : 0),
                    height: isTight ? 32 : 34,
                    fontWeight: 650,
                    fontSize: isXsTight ? 11.5 : (isTight ? 12 : 12.5),
                    whiteSpace: 'nowrap',
                    width: isTight ? 'calc(50% - 6px)' : 'auto',
                    maxWidth: '100%',
                    flexShrink: 0,
                    borderRadius: 1.25,
                    px: 1,
                  }}
                >
                  {t('tarifs.pricelist.filters.all', 'Tous')}
                </Button>

                {regularBrands
                  .slice()
                  .sort((a, b) => a.brand.localeCompare(b.brand))
                  .map((b) => (
                    <Button
                      key={b.id}
                      size="small"
                      variant={brandFilter === b.id ? 'contained' : 'outlined'}
                      onClick={() => setBrandFilter((prev) => (prev === b.id ? 'all' : b.id))}
                      sx={{
                        flex: isTight ? '1 1 calc(50% - 6px)' : (isSm ? '0 0 auto' : '1 1 0'),
                        minWidth: isXsTight ? 0 : (isSm ? 110 : 0),
                        height: isTight ? 32 : 34,
                        fontWeight: 650,
                        fontSize: isXsTight ? 11.5 : (isTight ? 12 : 12.5),
                        whiteSpace: 'nowrap',
                        width: isTight ? 'calc(50% - 6px)' : 'auto',
                        maxWidth: '100%',
                        flexShrink: 0,
                        borderRadius: 1.25,
                        px: 1,
                      }}
                    >
                      {b.brand.charAt(0) + b.brand.slice(1).toLowerCase()}
                    </Button>
                  ))}
              </Box>
            </Box>
          )}

          {/* Barre de recherche (sous les boutons) */}
          <TextField
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            size="small"
            fullWidth
            sx={{
              mt: 0.75,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.25,
                height: isTight ? 40 : 44,
              },
              '& .MuiOutlinedInput-input': {
                paddingTop: 0,
                paddingBottom: 0,
              },
            }}
            placeholder={t('tarifs.pricelist.search')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

      {visibleRegularBrands.length === 0 ? (
        <Box sx={{ p: 2, color: 'text.secondary' }}>
          {t('tarifs.pricelist.emptyBrands')}
        </Box>
      ) : (
        <>
          {/* ===== Featured ===== */}
          {(() => {
            const featured = featuredBrands;
            if (!featured || featured.length === 0) return null;
            return (
              <Box sx={{ ...subtleCell, p: 0.5, mx: 1, mb: 1.25 }}>
                <List dense>
                  {featured.map((b, fidx) => {
                    const key = `brand:${b.id}`;
                    // on force la quantité de ce lot marque entre 0 et 1
                    const rawQty = Number(cart[key] || 0) || 0;
                    const qty = rawQty > 0 ? 1 : 0;
                    const price = Number(b.pricingPrice) || 0;
                    const title = t('tarifs.pricelist.batchSort.title');
                    const desc = t('tarifs.pricelist.batchSort.desc');

                    const setQty = (next) => {
                      const v = next > 0 ? 1 : 0;
                      onSetQty?.(key, v);
                    };

                    // ✅ Ouvre la popup d'expertise dès qu'on sélectionne ce lot (0 -> 1)
                    const openExpertiseIfNeeded = (nextQty) => {
                      const isExpertise = (Number(price) || 0) <= 0;
                      if (!isExpertise) return;
                      if (!onRequestExpertise) return;

                      // On n'ouvre que lors de la transition 0 -> 1
                      if (qty === 0 && nextQty === 1) {
                        onRequestExpertise(key);
                      }
                    };

                    const handleRootClick = () => {
                      if (qty >= 1) return;
                      setQty(1);
                      openExpertiseIfNeeded(1);
                    };

                    const handleDec = (e) => {
                      e.stopPropagation();
                      if (qty <= 0) return;
                      setQty(0);
                    };

                    const handleInc = (e) => {
                      e.stopPropagation();
                      if (qty >= 1) return;
                      setQty(1);
                      openExpertiseIfNeeded(1);
                    };

                    const handleInputChange = (e) => {
                      const val = e.target.value;
                      const n = Number(val);
                      const v = Number.isNaN(n) ? 0 : n;

                      // On force 0/1 sur ce lot
                      const nextQty = v > 0 ? 1 : 0;
                      setQty(nextQty);
                      openExpertiseIfNeeded(nextQty);
                    };

                    return (
                      <React.Fragment key={`featured-${b.id}`}>
                        <ListItemButton
                          sx={{
                            ...itemSx,
                            ...(highlightedId === key ? highlightSx : null),
                          }}
                          disableRipple
                          onClick={handleRootClick}
                          data-price-id={key}
                        >
                          <ListItemText primary={title} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography sx={{ color: 'black', fontWeight: 600, fontSize: 13 }}>
                              {priceFmt(price, t)}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={handleDec}
                              disabled={qty === 0}
                            >
                              <RemoveRoundedIcon fontSize="small" />
                            </IconButton>
                            <TextField
                              size="small"
                              value={qty}
                              onClick={stop}
                              onMouseDown={stop}
                              onFocus={stop}
                              onChange={handleInputChange}
                              inputProps={{
                                inputMode: 'numeric',
                                style: { width: 40, textAlign: 'center', fontSize: 13 },
                                min: 0,
                                max: 1,
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={handleInc}
                              disabled={qty >= 1}
                            >
                              <AddRoundedIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </ListItemButton>

                        <Box sx={{ px: 1.25, pt: 0.25, pb: 0.5 }}>
                          <Typography sx={{ color: 'text.secondary', fontSize: 12.5, lineHeight: 1.35 }}>{desc}</Typography>
                        </Box>

                        {fidx < featured.length - 1 && <Divider component="li" sx={{ my: 0.15 }} />}
                      </React.Fragment>
                    );
                  })}
                </List>
              </Box>
            );
          })()}

          {/* ===== Marques + items (modèles ou composants) ===== */}
          <List dense>
            {visibleRegularBrands.map((b, idx) => {
              const isScreen = partFilter === 'screen';

              if (isScreen) {
                let mList = (modelsByBrand[b.id] || []).filter((m) => {
                  if (!q) return true;
                  const hay = `${b.brand} ${m.model}`.toLowerCase();
                  return hay.includes(q);
                });
                mList = sortModelsSmart(b.brand, mList);

                return (
                  <React.Fragment key={b.id}>
                    <ListItemButton sx={{ ...itemSx, cursor: 'default' }} disableRipple>
                      <ListItemText
                        primary={
                          <Typography component="span" sx={{ fontWeight: 700, fontSize: 13.5 }} noWrap>
                            {b.brand}
                          </Typography>
                        }
                      />
                    </ListItemButton>

                    <Box sx={{ ...subtleCell, p: 0.5, mx: 1, my: 0.4 }}>
                      {mList.length === 0 ? (
                        <Box sx={{ px: 1, py: 0.75 }}>
                          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                            {t('tarifs.pricelist.empty.noModelsForBrand')}
                          </Typography>
                        </Box>
                      ) : (
                        <List dense>
                          {mList.map((m, midx) => {
                            const key = `model:${m.id}`;
                            const qty = cart[key] || 0;
                            const price = Number(m.price) || 0;
                            const label = buildModelLabel(m);
                            const showQty = m.phone !== false;

                            return (
                              <React.Fragment key={m.id}>
                                <ListItemButton
                                  sx={{
                                    ...itemSx,
                                    ...(highlightedId === key ? highlightSx : null),
                                  }}
                                  disableRipple
                                  onClick={() => onAdd?.(key)}
                                  data-price-id={key}
                                >
                                  <ListItemText primary={label} />
                                  {showQty && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                      <Typography sx={{ color: 'black', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                                        {priceFmt(price, t)}
                                      </Typography>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); onRemove?.(key); }}
                                        disabled={qty === 0}
                                      >
                                        <RemoveRoundedIcon fontSize="small" />
                                      </IconButton>
                                      <TextField
                                        size="small"
                                        value={qty}
                                        onClick={stop}
                                        onMouseDown={stop}
                                        onFocus={stop}
                                        onChange={(e) => onSetQty?.(key, e.target.value)}
                                        inputProps={{ inputMode: 'numeric', style: { width: 40, textAlign: 'center', fontSize: 13 } }}
                                      />
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAdd?.(key); }}>
                                        <AddRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  )}
                                </ListItemButton>
                                {midx < mList.length - 1 && <Divider component="li" sx={{ my: 0.15 }} />}
                              </React.Fragment>
                            );
                          })}
                        </List>
                      )}
                    </Box>

                    {idx < visibleRegularBrands.length - 1 && (
                      <Divider component="li" sx={{ my: 0.2 }} />
                    )}
                  </React.Fragment>
                );
              }

              // components mode
              const cListAll = (componentsByBrand[b.id] || []);
              const cList = q
                ? cListAll.filter((c) => {
                    const modelName = String(c?._model?.model || '').toLowerCase();
                    const compName = String(c?.component ?? c?.name ?? c?.Component ?? '').toLowerCase();
                    const hay = `${b.brand} ${modelName} ${compName}`.toLowerCase();
                    return hay.includes(q);
                  })
                : cListAll;

              return (
                <React.Fragment key={b.id}>
                  <ListItemButton sx={{ ...itemSx, cursor: 'default' }} disableRipple>
                    <ListItemText
                      primary={
                        <Typography component="span" sx={{ fontWeight: 700, fontSize: 13.5 }} noWrap>
                          {b.brand}
                        </Typography>
                      }
                    />
                  </ListItemButton>

                  <Box sx={{ ...subtleCell, p: 0.5, mx: 1, my: 0.4 }}>
                    {cList.length === 0 ? (
                      <Box sx={{ px: 1, py: 0.75 }}>
                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                          {t('tarifs.pricelist.empty.noModelsForBrand')}
                        </Typography>
                      </Box>
                    ) : (
                      <List dense>
                        {cList.map((c, cidx) => {
                          const key = `component:${c.id}`;
                          const qty = cart[key] || 0;
                          const price = Number(c.unitPrice ?? 0) || 0;
                          const label = buildModelLabel(c._model);

                          return (
                            <React.Fragment key={c.id}>
                              <ListItemButton
                                sx={{
                                  ...itemSx,
                                  ...(highlightedId === key ? highlightSx : null),
                                }}
                                disableRipple
                                onClick={() => onAdd?.(key)}
                                data-price-id={key}
                              >
                                <ListItemText primary={label} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                  <Typography sx={{ color: 'black', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                                    {priceFmt(price, t)}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); onRemove?.(key); }}
                                    disabled={qty === 0}
                                  >
                                    <RemoveRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <TextField
                                    size="small"
                                    value={qty}
                                    onClick={stop}
                                    onMouseDown={stop}
                                    onFocus={stop}
                                    onChange={(e) => onSetQty?.(key, e.target.value)}
                                    inputProps={{ inputMode: 'numeric', style: { width: 40, textAlign: 'center', fontSize: 13 } }}
                                  />
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); onAdd?.(key); }}>
                                    <AddRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </ListItemButton>
                              {cidx < cList.length - 1 && <Divider component="li" sx={{ my: 0.15 }} />}
                            </React.Fragment>
                          );
                        })}
                      </List>
                    )}
                  </Box>

                  {idx < visibleRegularBrands.length - 1 && (
                    <Divider component="li" sx={{ my: 0.2 }} />
                  )}
                </React.Fragment>
              );
            })}
          </List>

          <Divider sx={{ mt: 1, mb: 1 }} />
          <Box sx={{ px: 1, pb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
              {t('tarifs.pricelist.disclaimer.short')}
            </Typography>
          </Box>
        </>
      )}
      </Paper>
    </Box>
  );
}